import Link from 'next/link';
import Image from 'next/image';
import { FormProvider } from 'react-hook-form';
import { RichText } from 'prismic-reactjs';

import Modal from 'react-modal';

import Checkbox from 'components/form/Checkbox';
import TextInput from 'components/form/TextInput';

import { newsletterHtmlSerializer } from 'utils/htmlSerializers/newsletterHtmlSerializer';

import { Routes } from 'models/enums/routes';

import { INewslatterProps } from './interfaces';
import { useNewsletter } from './newsletter.hooks';
import {
  Arrow,
  CheckboxContainer,
  CheckboxContainerButton,
  NewsletterAboutFirst,
  NewsletterAboutSecond,
  NewsletterFormContainer,
  NewsletterFullAgreement,
  NewsletterGridContainer,
  NewsletterImage,
  NewsletterInner,
  NewsletterModalButton,
  NewsletterModalButtonAccept,
  NewsletterModalOverlay,
  NewsletterModalText,
  NewsletterModalTextSecond,
  NewsletterModalTitle,
  NewsletterModalWrapper,
  NewsletterSubmitButton,
  NewsletterTitle,
  NewsletterWrapper,
} from './newsletter.styles';
import { imageSerializer } from '../../../utils/imageSerializer';

export const Newsletter = ({ dataNewsletter }: INewslatterProps) => {
  const {
    handleSubmit,
    formMethods,
    handleCloseAgreement,
    handleShowAgreement,
    newsletterRef,
    submitted,
    submitting,
    width,
    isAgreementShown,
    modalIsOpen,
    setIsModalOpen,
    agreementRef,
    agreementHeight,
    checkboxRef,
    agreementContainerRef,
  } = useNewsletter(dataNewsletter);

  return (
    <>
      <NewsletterWrapper ref={newsletterRef}>
        <NewsletterInner>
          <NewsletterGridContainer>
            <NewsletterTitle>
              {RichText.asText(dataNewsletter.title)}
            </NewsletterTitle>
            <NewsletterAboutFirst>
              {RichText.asText(dataNewsletter.about_text)}
            </NewsletterAboutFirst>
            <NewsletterImage>
              <svg
                width="200"
                height="129"
                viewBox="0 0 200 129"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M71.8266 1.0997C71.5144 0.963774 71.1593 0.966433 70.8492 1.10702L37.4861 16.2319H106.58L71.8266 1.0997ZM109.084 16.2319L72.2257 0.18318C71.6542 -0.0656436 71.0041 -0.0607768 70.4364 0.196575L35.0651 16.2319H15.1769C13.9601 16.2319 12.9737 17.2183 12.9737 18.4351V27.7487L0.223863 36.3286L0 36.4793L0.00312728 36.7491L1.06663 128.504L1.07236 128.999L1.56717 128.998L142.8 128.785L143.299 128.785V128.286V127.147C146.244 128.056 149.832 128.681 153.444 128.745C157.326 128.814 161.276 128.236 164.489 126.628L164.041 125.734C161.023 127.245 157.25 127.813 153.461 127.746C149.822 127.681 146.206 127.03 143.299 126.099V109.035C148.553 111.563 154.37 111.277 160.94 108.912L160.602 107.971C153.999 110.348 148.344 110.52 143.299 107.918V71.5294V67.5137C143.39 67.4682 143.503 67.4125 143.639 67.349C144.039 67.1629 144.63 66.9092 145.39 66.6447C146.911 66.1155 149.104 65.5442 151.777 65.3813C156.643 65.0849 161.477 66.0935 165.566 68.6886C169.665 71.2896 172.787 75.1808 174.441 79.7519C176.002 84.0635 176.014 88.8519 174.754 93.2863L174.091 92.4406L173.941 92.2493H173.698H156.111H155.05L155.726 93.0676L182.346 125.269L182.496 125.451H182.732H198.965H199.993L199.358 124.642L187.548 109.591C188.01 109.135 188.268 108.781 188.646 108.221C194.148 100.075 195.387 90.7656 194.143 81.4875C192.897 72.184 188.515 63.5866 181.726 57.1202C171.356 47.2447 157.622 44.4161 143.299 47.5963V35.2531V34.9652L143.05 34.8207L128.623 26.4496V18.4351C128.623 17.2183 127.637 16.2319 126.42 16.2319H109.084ZM145.062 65.7005C144.316 65.96 143.722 66.2107 143.299 66.405V48.6207C157.436 45.4223 170.898 48.1885 181.036 57.8441C187.661 64.1533 191.936 72.5421 193.153 81.6203C194.373 90.7239 193.148 99.7692 187.817 107.661C187.487 108.151 187.275 108.444 186.927 108.799L175.509 94.2486C177.023 89.448 177.103 84.1687 175.381 79.4117C173.651 74.6328 170.388 70.5643 166.102 67.8446C161.807 65.119 156.757 64.0765 151.717 64.3835C148.935 64.5529 146.652 65.1472 145.062 65.7005ZM128.623 42.9008L141.804 35.2531L128.623 27.6054V42.9008ZM142.3 36.1209L128.389 44.1924L91.0161 69.4098L142.3 126.973V71.5294V36.1209ZM141.686 127.787L90.1703 69.9631L74.9643 79.4994C73.6997 80.2925 72.0937 80.2956 70.826 79.5075L55.4693 69.9596L2.69797 127.997L141.686 127.787ZM2.05147 127.222L1.01255 37.5877L13.2242 44.202L54.6026 69.4269L2.05147 127.222ZM53.3023 67.4635L55.6398 68.8885L55.6435 68.8908L61.3863 72.4613C61.3948 72.4609 61.4034 72.4606 61.4121 72.4606L84.3064 72.4606L89.9899 68.8963L127.623 43.5029V26.7375V18.4351C127.623 17.7704 127.085 17.2315 126.42 17.2315H108.98H35.1731H15.1769C14.5122 17.2315 13.9733 17.7704 13.9733 18.4351V28.0148V43.4879L31.171 53.9719C31.1829 53.9711 31.1949 53.9707 31.207 53.9707H86.3782C86.6542 53.9707 86.878 54.1944 86.878 54.4705C86.878 54.7465 86.6542 54.9703 86.3782 54.9703H32.8087L41.0059 59.9674H97.115C97.3911 59.9674 97.6148 60.1912 97.6148 60.4672C97.6148 60.7433 97.3911 60.9671 97.115 60.9671H42.6456L51.6625 66.4639H86.3803C86.6563 66.4639 86.8801 66.6877 86.8801 66.9637C86.8801 67.2397 86.6563 67.4635 86.3803 67.4635H53.3023ZM82.7124 73.4603L62.993 73.4603L71.3538 78.6585C72.2971 79.245 73.4922 79.2427 74.4332 78.6525L82.7124 73.4603ZM12.9737 42.9295V28.9536L1.46693 36.697L12.9737 42.9295ZM157.173 93.2489L182.967 124.451H197.938L173.454 93.2489H157.173ZM27.9608 35.98C27.6847 35.98 27.4609 36.2038 27.4609 36.4798C27.4609 36.7558 27.6847 36.9796 27.9608 36.9796H97.1119C97.3879 36.9796 97.6117 36.7558 97.6117 36.4798C97.6117 36.2038 97.3879 35.98 97.1119 35.98H27.9608ZM28.4609 41.9772C28.1849 41.9772 27.9611 42.201 27.9611 42.477C27.9611 42.7531 28.1849 42.9768 28.4609 42.9768H86.3781C86.6542 42.9768 86.878 42.7531 86.878 42.477C86.878 42.201 86.6542 41.9772 86.3781 41.9772H28.4609ZM28.4609 47.9739C28.1849 47.9739 27.9611 48.1977 27.9611 48.4737C27.9611 48.7498 28.1849 48.9735 28.4609 48.9735H97.1128C97.3888 48.9735 97.6126 48.7498 97.6126 48.4737C97.6126 48.1977 97.3888 47.9739 97.1128 47.9739H28.4609Z"
                  fill="url(#paint0_linear)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear"
                    x1="22.4316"
                    y1="128.999"
                    x2="73.0618"
                    y2="-60.9264"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#0E4ED7" />
                    <stop offset="0.884917" stopColor="#F94743" />
                  </linearGradient>
                </defs>
              </svg>
            </NewsletterImage>

            <NewsletterFormContainer className="newsletter__form-container">
              <FormProvider {...formMethods}>
                <form
                  noValidate
                  onSubmit={formMethods.handleSubmit(handleSubmit)}
                >
                  <fieldset className="fieldset newsletter__fieldset">
                    <TextInput
                      additionalClassName="newsletter__input"
                      name="firstname"
                      label={RichText.asText(dataNewsletter.firstname_label)}
                      type="text"
                    />

                    <TextInput
                      additionalClassName="newsletter__input"
                      name="email"
                      label={RichText.asText(dataNewsletter.email_label)}
                      type="email"
                    />
                  </fieldset>
                  <CheckboxContainer>
                    <Checkbox
                      additionalClassName="newsletter__checkbox"
                      name="agreement"
                      label={
                        <>
                          {RichText.asText(dataNewsletter.agreement_text)}
                          <Link href={Routes.DocumentsPrivacyPolicy}>
                            {RichText.asText(dataNewsletter.privacy_policy)}
                          </Link>
                          .
                        </>
                      }
                    />
                    <CheckboxContainerButton
                      aria-label="Show Agreement"
                      onClick={(e) => handleShowAgreement(e)}
                      ref={checkboxRef}
                    >
                      <Image
                        {...imageSerializer(dataNewsletter.info_button_img)}
                      />
                    </CheckboxContainerButton>
                  </CheckboxContainer>
                  <NewsletterSubmitButton
                    aria-label="submit"
                    type="submit"
                    disabled={submitting || submitted}
                  >
                    {RichText.asText(dataNewsletter.button_label)}
                  </NewsletterSubmitButton>
                </form>
              </FormProvider>
            </NewsletterFormContainer>
            {width && width < 769 && (
              <NewsletterModalOverlay isVisible={isAgreementShown} />
            )}
            <NewsletterFullAgreement
              ref={agreementContainerRef}
              isVisible={isAgreementShown}
              style={{
                top: `${newsletterRef?.current?.clientHeight / 2}px`,
                maxHeight: `${agreementHeight}px`,
              }}
            >
              {width && width >= 1200 && <Arrow />}
              <div ref={agreementRef}>
                <RichText
                  render={dataNewsletter.agreement_text2}
                  htmlSerializer={newsletterHtmlSerializer}
                />
                <NewsletterModalButton onClick={(e) => handleCloseAgreement(e)}>
                  {RichText.asText(dataNewsletter.button_confirmation_label)}
                </NewsletterModalButton>
              </div>
              {width && width < 1200 && (
                <Arrow
                  positionXlg={
                    (newsletterRef?.current?.clientWidth -
                      checkboxRef?.current?.offsetLeft) /
                      2 -
                    7 -
                    64
                  }
                  positionX={
                    (newsletterRef?.current?.clientWidth -
                      checkboxRef?.current?.offsetLeft) /
                      2 -
                    7
                  }
                />
              )}
            </NewsletterFullAgreement>
            <NewsletterAboutSecond>
              {RichText.asText(dataNewsletter.about_text2)}
            </NewsletterAboutSecond>
          </NewsletterGridContainer>
        </NewsletterInner>
      </NewsletterWrapper>
      <Modal
        isOpen={modalIsOpen}
        ariaHideApp={false}
        preventScroll
        closeTimeoutMS={500}
      >
        <NewsletterModalWrapper>
          <svg
            width="81"
            height="54"
            viewBox="0 0 81 54"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M81 2.00328L27.6654 54L0 27.0286L2.05482 25.0254L27.6654 49.9935L78.9452 0L81 2.00328Z"
              fill="url(#paint0_linear)"
            />
            <defs>
              <linearGradient
                id="paint0_linear"
                x1="18.5266"
                y1="120.214"
                x2="116.27"
                y2="58.986"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0E4ED7" />
                <stop offset="1" stopColor="#F94743" />
              </linearGradient>
            </defs>
          </svg>
          <NewsletterModalTitle>
            {RichText.asText(dataNewsletter.modal_title)}
          </NewsletterModalTitle>
          <NewsletterModalText>
            {RichText.asText(dataNewsletter.modal_text)}
          </NewsletterModalText>
          <NewsletterModalButtonAccept onClick={() => setIsModalOpen(false)}>
            {RichText.asText(dataNewsletter.button_confirmation_label)}
          </NewsletterModalButtonAccept>
          <NewsletterModalTextSecond>
            {RichText.asText(dataNewsletter.modal_text_second)}
          </NewsletterModalTextSecond>
        </NewsletterModalWrapper>
      </Modal>
    </>
  );
};
